import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import Dinero from 'dinero.js'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { Box, Button, Typography, Stack } from '@mui/material'
import { Add as AddIcon, WarningAmberSharp as WarningAmberSharpIcon } from '@mui/icons-material'
import { randomId } from '@mui/x-data-grid-generator'
import { useTheme } from '@mui/material/styles'
import DialogModal from 'components/generic/modals/dialog-modal'
import PaymentMethodRow from 'components/containers/inbound-tickets/components/PaymentMethodRow'
import Loading from 'components/generic/Loading'
import { groupBy } from 'lodash'
import { useCreatePaymentMutation } from 'services/payments'
import {
  BILL_ACTION,
  ALL_PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUSES,
  BILL_TYPES,
  ROUNDING_PRECISIONS,
  ACCOUNT_PAYMENT_TYPE_ENUMS,
  TICKET_STATUSES,
} from 'utils/constants'
import { formatNumber, setDecimalPrecision, dispatchSnackbar } from 'utils/helpers'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { v4 as uuidv4 } from 'uuid'
import { useFilteredPaymentMethods } from 'utils/custom-hooks/useFilteredPaymentMethods'

const getNewPaymentMethod = (method = 'cash') => {
  const rowId = randomId()

  return {
    id: rowId,
    name: rowId,
    method,
  }
}

const BillPaymentsModal = ({
  billData,
  customerData,
  billTaxRate,
  paymentsData,
  selectedTicketsData,
  isLoading,
  isMergingAndCreatingPayment,
  getBillValues,
  onClose,
  dispatchFormSubmitEvent,
  pricePrepend,
  ...restProps
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()

  const [paymentMethods, setPaymentMethods] = useState([])
  const [roundingDelta, setRoundingDelta] = useState(0.0)
  const [totalNetValue, setTotalNetValue] = useState(0)
  const [totalPaymentAmount, setTotalPaymentAmount] = useState(0)
  const [balanceRemaining, setBalanceRemaining] = useState(0)

  const idempotencyKey = useRef([uuidv4()])

  const [createPayment, { isLoading: isCreatingPayment }] = useCreatePaymentMutation()

  const { control, handleSubmit, getValues, setValue, watch } = useForm()
  const { defaultCurrency, settings, hasAtm, details, selectedCashDrawerId, cashDrawers } = useSelector(
    state => state.settings,
  )

  const filteredCashDrawers = useMemo(() => {
    return cashDrawers?.filter(drawer => drawer?.type !== ACCOUNT_PAYMENT_TYPE_ENUMS.ATM)
  }, [cashDrawers])
  const yardPreferredPaymentMethod = settings?.purchaseSettings?.payment?.yardPreferredPaymentMethod

  const paymentMethodsData = useMemo(
    () => (billData?.paymentMethods ? groupBy(billData.paymentMethods, pm => pm.paymentMethod) : {}),
    [billData],
  )

  const PAYMENT_METHOD_OPTIONS = useFilteredPaymentMethods({
    hasAtm,
    customerGsPayContactId: customerData?.gsPayContactId,
    paymentMethodsData,
    paymentMethods,
  })

  const paymentMethodIsGreensparkPay = useMemo(
    () => paymentMethods.some(pm => pm.method === 'gs_pay'),
    [paymentMethods],
  )

  useEffect(() => {
    // Exit early if there are no payment methods in the billId
    if (!paymentMethods || paymentMethods.length === 0) {
      setRoundingDelta(0.0)
      return
    }

    // Only apply rounding when all splits use methods that have rounding
    let round = true
    paymentMethods.forEach(pm => {
      if (paymentMethodsData[pm.method][0].roundingDelta === null) {
        round = false
      }
    })

    if (round) {
      try {
        // since all payment methods have rounding, we can just use the first one
        // Note that once we have support for different rounding per payment method, this will need to change
        const delta = paymentMethodsData[paymentMethods[0].method][0].roundingDelta || 0.0
        setRoundingDelta(Number(delta))
      } catch (e) {
        setRoundingDelta(0.0)
      }
    } else {
      setRoundingDelta(0.0)
    }
  }, [paymentMethods, totalPaymentAmount])

  const billType = useMemo(() => billData?.type || BILL_TYPES.MATERIAL.id, [billData])

  const getNextAvailablePaymentMethod = useCallback(() => {
    const customerPreferredPayment = customerData?.preferredPaymentMethod?.toLowerCase()

    return customerPreferredPayment || yardPreferredPaymentMethod || 'cash'
  }, [PAYMENT_METHOD_OPTIONS, paymentMethods, paymentsData])

  useEffect(() => {
    setPaymentMethods([getNewPaymentMethod(getNextAvailablePaymentMethod())])
  }, [])

  useEffect(() => {
    const paymentMethodsSet = new Set(paymentMethods.map(pm => pm.method))
    const netSubtotal = selectedTicketsData
      .reduce((aggregate, ticket) => {
        const ticketSubtotal =
          billType === BILL_TYPES.MATERIAL.id ? Dinero(ticket.netValue).toUnit() : ticket.freightCost?.cost || 0
        const feeOrPromotionAmount = ticket.feeOrPromotion?.reduce((acc, feeOrPromotion) => {
          const shouldApplyFeeOrPromotion = feeOrPromotion.eligiblePaymentTypes.some(method =>
            paymentMethodsSet.has(method),
          )
          if (!shouldApplyFeeOrPromotion) return acc
          return (acc += feeOrPromotion?.calculatedValue || 0)
        }, 0)
        return aggregate + ticketSubtotal + (feeOrPromotionAmount || 0)
      }, 0)
      .toFixed(2)
    const netSubtotalRoundedWithDelta = parseFloat(netSubtotal) + Number(roundingDelta ?? 0)
    const netTax = roundTo((netSubtotalRoundedWithDelta * billTaxRate) / 100, ROUNDING_PRECISIONS.COST)

    setTotalNetValue(
      setDecimalPrecision(netSubtotalRoundedWithDelta + netTax - (billData?.total || 0), {
        shouldFormatNumber: false,
        decimalPrecisionOverride: 2,
      }),
    )
  }, [billData, selectedTicketsData, billTaxRate, billType, roundingDelta, paymentMethods])

  useEffect(() => {
    setBalanceRemaining(
      setDecimalPrecision(Number(totalNetValue) - Number(totalPaymentAmount), {
        shouldFormatNumber: false,
        decimalPrecisionOverride: 2,
      }),
    )
  }, [totalNetValue, totalPaymentAmount, roundingDelta])

  const onSubmit = async data => {
    const billId =
      billData?.id ||
      [...selectedTicketsData].sort((a, b) => {
        const ticketIdA = isNaN(Number(a.ticketId)) ? a.ticketId : Number(a.ticketId)
        const ticketIdB = isNaN(Number(b.ticketId)) ? b.ticketId : Number(b.ticketId)
        if (ticketIdA < ticketIdB) return 1
        if (ticketIdA > ticketIdB) return -1
        return 0
      })?.[0]?.billId
    const formattedPayments = Object.values(data?.payment)
      .filter(payment => !payment.disabled)
      .map((paymentData, index) => ({
        billId,
        paymentDate: (paymentData?.paymentDate || new Date()).getTime(),
        completionDate: new Date(getBillValues('details.completionDate')).getTime(),
        paymentMethod: paymentData?.method,
        accountId: paymentData?.method === 'cash' ? selectedCashDrawerId || filteredCashDrawers?.[0]?.id : undefined,
        total: {
          amount: paymentData?.amount * 100,
          currency: getBillValues('details.currency') || defaultCurrency || '',
          precision: 2,
        },
        status: PAYMENT_STATUSES.PAID.id,
        inboundTickets:
          billType === BILL_TYPES.MATERIAL.id ? selectedTicketsData?.map(inboundTicket => inboundTicket?.id) : [],
        idempotencyKey: idempotencyKey?.current[index],
      }))
    const multiplePayments = formattedPayments.length > 1

    if (!billData?.id) {
      dispatchFormSubmitEvent(BILL_ACTION.PAY, formattedPayments)
    } else {
      try {
        const createPaymentResponses = await formattedPayments.reduce(async (prevPromise, formattedPayment) => {
          const results = await prevPromise
          const response = await createPayment({
            billId,
            batchBillId: billData?.batchBillId,
            paymentCreation: formattedPayment,
          })
          return [...results, response]
        }, Promise.resolve([]))
        const createPaymentError = createPaymentResponses.find(response => !!response.error)?.error
        if (createPaymentError) throw new Error(createPaymentError?.data?.error)
        dispatchSnackbar(dispatch, 'success', `Payment${multiplePayments ? 's' : ''} confirmed`)
      } catch (error) {
        const message =
          error?.data?.error || error?.message || `An error occurred making the payment${multiplePayments ? 's' : ''}`
        dispatchSnackbar(dispatch, 'error', message)
      }
      onClose()
    }
  }

  const recalculateTotalPaymentAmount = useCallback(
    rowId => event => {
      const paymentMethodRows = Object.entries(getValues('payment') || {}).filter(([, payment]) => !payment.disabled)
      const newTotalPaymentAmount = paymentMethodRows.reduce((aggregate, [key, rowData]) => {
        const { amount = 0 } = rowData
        const value = rowId === key ? event?.target?.value || 0 : amount
        return aggregate + Number(value)
      }, 0)
      setTotalPaymentAmount(newTotalPaymentAmount)
    },
    [totalPaymentAmount],
  )

  const onClickAddPaymentMethod = () => {
    idempotencyKey.current.push(uuidv4())
    setPaymentMethods([...paymentMethods, getNewPaymentMethod(getNextAvailablePaymentMethod())])
  }

  const onClickDeletePaymentMethod = paymentMethod => {
    idempotencyKey.current.pop()
    const newPaymentMethods = paymentMethods.filter(pm => pm.id !== paymentMethod.id)
    setPaymentMethods(newPaymentMethods)
    setValue(`payment.${paymentMethod.id}.amount`, 0)
    setValue(`payment.${paymentMethod.id}.disabled`, true)
    recalculateTotalPaymentAmount()()
  }

  const renderPaymentMethodRows = () =>
    paymentMethods.map(paymentMethod => (
      <PaymentMethodRow
        key={paymentMethod.id}
        name={`payment.${paymentMethod.id}`}
        defaultMethod={paymentMethod.method}
        availableMethods={PAYMENT_METHOD_OPTIONS}
        totalNetValue={parseFloat(totalNetValue)}
        control={control}
        watch={watch}
        setValue={setValue}
        paymentMethods={paymentMethods}
        setPaymentMethods={setPaymentMethods}
        onClickDelete={() => onClickDeletePaymentMethod(paymentMethod)}
        recalculateTotalPaymentAmount={recalculateTotalPaymentAmount}
        showPaymentDate
        pricePrepend={pricePrepend}
      />
    ))

  return (
    <Box>
      {(isCreatingPayment || isMergingAndCreatingPayment) && <Loading />}
      <DialogModal
        {...restProps}
        onClose={onClose}
        onSave={handleSubmit(onSubmit)}
        saveButtonProps={{
          loading: isCreatingPayment || isMergingAndCreatingPayment,
        }}
      >
        {totalPaymentAmount < 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              background: theme.palette.warning.light,
              alignItems: 'center',
              borderRadius: 1,
              padding: 2,
              marginBottom: 1,
            }}
          >
            <WarningAmberSharpIcon />
            <Typography variant="body2">You are about to pay a bill with a negative balance</Typography>
          </Stack>
        )}
        <Stack>{renderPaymentMethodRows()}</Stack>
        {!paymentMethodIsGreensparkPay && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button startIcon={<AddIcon />} onClick={onClickAddPaymentMethod}>
              ADD NEW PAYMENT TYPE
            </Button>
            <Typography
              pr={3}
              sx={{
                color: Number(isLoading ? 0 : balanceRemaining) !== 0 ? theme.palette.error.main : 'unset',
              }}
            >
              {`${pricePrepend}${formatNumber(isLoading ? '0.00' : balanceRemaining)} balance remaining`}
            </Typography>
          </Stack>
        )}
        {paymentMethodIsGreensparkPay && (
          <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
            GreenSpark Pay does not currently support adding additional payment methods.
          </Typography>
        )}
      </DialogModal>
    </Box>
  )
}

BillPaymentsModal.propTypes = {
  billData: PropTypes.object,
  billTaxRate: PropTypes.number,
  customerData: PropTypes.object,
  paymentsData: PropTypes.array,
  selectedTicketsData: PropTypes.array,
  isLoading: PropTypes.bool,
  isMergingAndCreatingPayment: PropTypes.bool,
  getBillValues: PropTypes.func,
  onClose: PropTypes.func,
  dispatchFormSubmitEvent: PropTypes.func,
  pricePrepend: PropTypes.string,
}

BillPaymentsModal.defaultProps = {
  billData: {},
  customerData: {},
  billTaxRate: 0,
  paymentsData: [],
  selectedTicketsData: [],
  isLoading: false,
  isMergingAndCreatingPayment: false,
  getBillValues: () => {},
  onClose: () => {},
  dispatchFormSubmitEvent: PropTypes.func,
  pricePrepend: '$',
}

export default BillPaymentsModal
